<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import KgrModal from './KgrModal.vue';
import KgrModalContent from './KgrModalContent.vue';
import KgrModalFooter from './KgrModalFooter.vue';
import KgrModalHeader from './KgrModalHeader.vue';

// 1:1-Port der Angular-Komponente `kgr-confirmation-modal`
// (customer-apps/confirmation), aufgesetzt auf KgrModal.
const props = withDefaults(
    defineProps<{
        show: boolean
        showDeclineButton?: boolean
        cancelButtonLabel?: string
        confirmButtonLabel?: string
        declineButtonLabel?: string
        confirmButtonCssClass?: string
        declineButtonCssClass?: string
        headline?: string
        text?: string
        fullWidth?: boolean
        fullHeight?: boolean
        width?: string
        height?: string
        loading?: boolean
    }>(),
    {
        showDeclineButton: false,
        cancelButtonLabel: '',
        confirmButtonLabel: '',
        declineButtonLabel: '',
        confirmButtonCssClass: 'kgr-button--accent',
        declineButtonCssClass: 'kgr-button--accent',
        headline: '',
        text: '',
        fullWidth: false,
        fullHeight: false,
        loading: false,
    },
);

const emit = defineEmits<{ confirm: [], decline: [], cancel: [] }>();

const { t } = useI18n();

// Defaults wie im Angular-Original ($localize), aber locale-reaktiv via t().
const headlineText = computed(() => props.headline || t('Bitte bestätigen'));
const bodyText = computed(() => props.text || t('Sind Sie sicher?'));
const cancelLabel = computed(() => props.cancelButtonLabel || t('Abbrechen'));
const confirmLabel = computed(() => props.confirmButtonLabel || t('Ja'));
const declineLabel = computed(() => props.declineButtonLabel || t('Nein'));
</script>

<template>
    <KgrModal
        :open="show"
        :close-on-backdrop-click="false"
        :width="width"
        :height="height"
        :full-width="fullWidth"
        :full-height="fullHeight"
        :loading="loading"
        @close="emit('cancel')"
    >
        <KgrModalHeader
            :headline="headlineText"
            :close-label="cancelLabel"
            @close="emit('cancel')"
        />

        <!-- Angulars `kgr-confirmation-modal` laesst `kgr-modal-content` auf seinen Defaults, also
             scrollbar — hier bewusst ebenso. Ohne `--scrollable` gilt `overflow-y: hidden`, und ein
             langer Text wird am `max-height` des Dialogs abgeschnitten statt scrollbar zu sein. -->
        <KgrModalContent>
            <slot>{{ bodyText }}</slot>
        </KgrModalContent>

        <KgrModalFooter class="kgr-postfix kgr-postfix--gutters kgr-postfix--center">
            <div class="kgr-postfix__fixed">
                <!-- `type="reset"` wie im Original (confirmation-modal.component.html:20). Ohne umschliessendes
                     <form> aktuell folgenlos, aber die Abweichung kostet nichts. -->
                <button
                    type="reset"
                    class="kgr-button kgr-button--outline"
                    @click="emit('cancel')"
                >
                    {{ cancelLabel }}
                </button>
            </div>
            <div class="kgr-postfix__fluid"></div>
            <div
                v-if="showDeclineButton"
                class="kgr-postfix__fixed"
            >
                <button
                    type="button"
                    class="kgr-button"
                    :class="declineButtonCssClass"
                    @click="emit('decline')"
                >
                    {{ declineLabel }}
                </button>
            </div>
            <div class="kgr-postfix__fixed">
                <button
                    type="button"
                    class="kgr-button"
                    :class="confirmButtonCssClass"
                    @click="emit('confirm')"
                >
                    {{ confirmLabel }}
                </button>
            </div>
        </KgrModalFooter>
    </KgrModal>
</template>
